import type { ComponentProps } from 'react';
import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { FieldLabelRow } from './field-label-row';
import { MultiCombobox } from '../../custom/multi-combobox';
import { cn } from '../../lib/utils';

export default function MultiComboboxTextField(
	props: ComponentProps<'select'> & {
		label: string;
		search: string;
		empty: string;
		options: { value: string; label: string }[];
		creatable?: boolean;
		createLabel?: (value: string) => string;
		onCreateNew?: (value: string) => void;
		variant?: 'default' | 'outline';
		orientation?: 'vertical' | 'horizontal';
	},
) {
	const field = useFieldContext<string[]>();
	const formReadOnly = useFormReadOnly();
	const isHorizontal = props.orientation === 'horizontal';
	const isOptional = useIsFieldOptional(field.name);

	return (
		<div
			className={cn(
				props.className,
				formReadOnly &&
					'pointer-events-none [&_[data-slot=combobox-chips]]:border-transparent [&_[data-slot=combobox-chips]]:px-0 [&_[data-slot=combobox-chips]]:focus-within:ring-0 [&_[data-slot=combobox-chip-input]]:min-w-0 [&_[data-slot=combobox-chip-input]]:flex-[0] [&_[data-slot=combobox-chip-input]]:opacity-0 [&_[data-slot=combobox-chip-input]]:overflow-hidden [&_[data-slot=combobox-chip-remove]]:opacity-0 [&_[data-slot=combobox-chip-remove]]:w-0 [&_[data-slot=combobox-chip-remove]]:-ml-2 [&_[data-slot=combobox-chip]]:h-auto [&_[data-slot=combobox-chip]]:px-0',
			)}
		>
			<div className={isHorizontal ? 'flex items-start gap-3' : 'grid gap-2'}>
				{props.label && (
					<FieldLabelRow
						label={props.label}
						htmlFor={field.name}
						hasError={field.state.meta.isTouched && !field.state.meta.isValid}
						optional={isOptional}
						className={cn('flex items-center justify-between', isHorizontal && 'w-36 shrink-0')}
					/>
				)}
				<div className={isHorizontal ? 'flex-1' : undefined}>
					<MultiCombobox
						id={field.name}
						isError={hasFieldError(field.state.meta)}
						placeholder={props.label}
						commandEmpty={props.empty}
						searchPlaceholder={props.search}
						creatable={props.creatable}
						createLabel={props.createLabel}
						onCreateNew={props.onCreateNew}
						variant={props.variant}
						readOnly={formReadOnly}
						options={props.options}
						value={field.state.value}
						onChange={field.handleChange}
					/>
					<FieldInfo fieldState={field.state} />
				</div>
			</div>
		</div>
	);
}
